import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MainTemplate from "../../components/MainTemplate/MainTemplate";
import CardBackground from "../../components/MainTemplate/CardBackground";
import { FaClock, FaMapMarkerAlt, FaPhone, FaChurch } from "react-icons/fa";
import { MdEventAvailable, MdDescription } from "react-icons/md";
import { BsCalendarDate } from "react-icons/bs";
import {
  HiOutlineArrowLeft,
  HiOutlineClock,
  HiOutlineLocationMarker,
  HiOutlinePhone,
  HiOutlineCalendar,
  HiOutlineInformationCircle,
  HiOutlineX,
  HiOutlineZoomIn,
  HiOutlineShare,
  HiOutlinePlus,
} from "react-icons/hi";
import placeholderImage from "../../images/event-placeholder-image.jpg";

const EventDetailView = () => {
  const { state } = useLocation();
  const edetails = state;
  const navigate = useNavigate();

  return (
    <MainTemplate>
      <CardBackground>
        <EventDetailViewChild edetails={edetails} navigate={navigate} />
      </CardBackground>
    </MainTemplate>
  );
};

const EventDetailViewChild = ({ edetails, navigate }) => {
  const [showImagePopup, setShowImagePopup] = React.useState(false);
  const [showShareMenu, setShowShareMenu] = React.useState(false);

  const imageUrl = `${process.env.REACT_APP_REPORTS_DOWNLOAD_URL}uploads/event/${edetails?.image}`;

  const handleAddToCalendar = () => {
    const startDate = new Date(edetails?.view_event_start_date);
    const endDate = new Date(edetails?.view_event_end_date);

    const eventDetails = {
      title: edetails?.title,
      description: edetails?.description,
      location: `${edetails?.event_location}, ${edetails?.zipcode}`,
      start: startDate.toISOString(),
      end: endDate.toISOString(),
    };

    // Google Calendar URL
    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      eventDetails.title
    )}&dates=${startDate
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}/g, "")}/${endDate
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}/g, "")}&details=${encodeURIComponent(
      eventDetails.description
    )}&location=${encodeURIComponent(eventDetails.location)}`;

    // Open in new tab
    window.open(googleUrl, "_blank");
  };

  const handleShare = async (platform) => {
    const eventUrl = window.location.href;
    const shareText = `Check out this event: ${edetails?.title}`;

    switch (platform) {
      case "copy":
        try {
          await navigator.clipboard.writeText(eventUrl);
          // toast.success('Link copied to clipboard!');
        } catch (err) {
          // toast.error('Failed to copy link');
        }
        break;
      case "whatsapp":
        window.open(
          `https://wa.me/?text=${encodeURIComponent(
            shareText + " " + eventUrl
          )}`,
          "_blank"
        );
        break;
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            eventUrl
          )}`,
          "_blank"
        );
        break;
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            shareText
          )}&url=${encodeURIComponent(eventUrl)}`,
          "_blank"
        );
        break;
    }
    setShowShareMenu(false);
  };

  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Image Popup */}
      {showImagePopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
          onClick={() => setShowImagePopup(false)}
        >
          <div className="relative w-full max-w-5xl">
            <button
              className="absolute p-2 text-white transition-colors rounded-full top-4 right-4 bg-black/50 hover:bg-black/70"
              onClick={(e) => {
                e.stopPropagation();
                setShowImagePopup(false);
              }}
            >
              <HiOutlineX className="w-6 h-6" />
            </button>
            {edetails?.image?.length ? (
              <img
                src={imageUrl}
                alt={edetails.title}
                className="w-full h-auto rounded-lg"
                crossOrigin="anonymous"
              />
            ) : (
              <img
                src={placeholderImage}
                alt={edetails?.title}
                className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
              />
            )}
          </div>
        </div>
      )}

      <div className="w-full px-4 py-6 sm:py-8">
        {/* Back Button */}
        <div className="w-full mx-auto mb-6 max-w-7xl">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 text-gray-600 transition-colors bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:text-gray-900"
          >
            <HiOutlineArrowLeft className="w-5 h-5 mr-2" />
            <span className="font-medium">Back to Events</span>
          </button>
        </div>

        {/* Event Header */}
        <div className="w-full mx-auto max-w-7xl">
          <div className="p-4 mb-6 bg-white border border-gray-200 shadow-sm rounded-xl sm:p-6 sm:mb-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 text-sm font-medium text-blue-600 rounded-full bg-blue-50">
                    {edetails?.event_type?.label}
                  </span>
                  <span
                    className={`px-3 py-1 text-sm font-medium rounded-full 
                    ${
                      edetails?.sponsorship_status?.value === "Open"
                        ? "text-green-600 bg-green-50"
                        : "text-yellow-600 bg-yellow-50"
                    }`}
                  >
                    {edetails?.sponsorship_status?.label}
                  </span>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                  {edetails?.title}
                </h1>
                <div className="flex items-center text-gray-600">
                  <FaChurch className="flex-shrink-0 w-5 h-5 mr-2 text-gray-400" />
                  <span className="font-medium">
                    {edetails?.church_id?.church_name}
                  </span>
                </div>
              </div>
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                {/* Add to Calendar Button */}
                <button
                  onClick={handleAddToCalendar}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 transition-colors rounded-lg bg-blue-50 hover:bg-blue-100"
                >
                  <HiOutlineCalendar className="w-5 h-5 mr-2" />
                  Add to Calendar
                </button>

                {/* Share Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 transition-colors rounded-lg bg-gray-50 hover:bg-gray-100"
                  >
                    <HiOutlineShare className="w-5 h-5 mr-2" />
                    Share
                  </button>

                  {/* Share Menu Dropdown */}
                  {showShareMenu && (
                    <div className="absolute right-0 z-10 w-48 py-1 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg">
                      <button
                        onClick={() => handleShare("copy")}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Copy Link
                      </button>
                      <button
                        onClick={() => handleShare("whatsapp")}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Share on WhatsApp
                      </button>
                      <button
                        onClick={() => handleShare("facebook")}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Share on Facebook
                      </button>
                      <button
                        onClick={() => handleShare("twitter")}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Share on Twitter
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Event Image */}
          <div
            className="group relative h-[300px] sm:h-[400px] rounded-xl overflow-hidden shadow-lg mb-6 sm:mb-8 cursor-pointer"
            onClick={() => setShowImagePopup(true)}
          >
            {edetails?.image?.length ? (
              <img
                crossOrigin="anonymous"
                src={imageUrl}
                alt={edetails?.title}
                className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
              />
            ) : (
              <img
                src={placeholderImage}
                alt={edetails?.title}
                className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/30"></div>
            {/* Zoom Icon Overlay */}
            <div className="absolute inset-0 flex items-center justify-center transition-opacity opacity-0 bg-black/40 group-hover:opacity-100">
              <HiOutlineZoomIn className="w-12 h-12 text-white" />
            </div>
          </div>

          {/* Event Details Grid */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 sm:gap-6 lg:gap-8">
            {/* Left Column */}
            <div className="space-y-4 sm:space-y-6 lg:space-y-8">
              {/* Description */}
              <div className="p-4 bg-white border border-gray-200 shadow-sm rounded-xl sm:p-6">
                <div className="flex items-center mb-4">
                  <HiOutlineInformationCircle className="flex-shrink-0 w-6 h-6 mr-3 text-blue-500" />
                  <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
                    Description
                  </h2>
                </div>
                <p className="leading-relaxed text-gray-600">
                  {edetails?.description}
                </p>
              </div>

              {/* Date & Time */}
              <div className="p-4 bg-white border border-gray-200 shadow-sm rounded-xl sm:p-6">
                <div className="flex items-center mb-4">
                  <HiOutlineCalendar className="flex-shrink-0 w-6 h-6 mr-3 text-blue-500" />
                  <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
                    Date & Time
                  </h2>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start text-gray-600">
                    <HiOutlineClock className="flex-shrink-0 w-5 h-5 mt-1 mr-3 text-gray-400" />
                    <div>
                      <p className="font-medium">Start Time</p>
                      <p className="text-sm sm:text-base">
                        {new Date(
                          edetails?.view_event_start_date
                        ).toLocaleString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start text-gray-600">
                    <HiOutlineClock className="flex-shrink-0 w-5 h-5 mt-1 mr-3 text-gray-400" />
                    <div>
                      <p className="font-medium">End Time</p>
                      <p className="text-sm sm:text-base">
                        {new Date(edetails?.view_event_end_date).toLocaleString(
                          "en-US",
                          {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          }
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4 sm:space-y-6 lg:space-y-8">
              {/* Location Details */}
              <div className="p-4 bg-white border border-gray-200 shadow-sm rounded-xl sm:p-6">
                <div className="flex items-center mb-4">
                  <HiOutlineLocationMarker className="flex-shrink-0 w-6 h-6 mr-3 text-blue-500" />
                  <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
                    Location
                  </h2>
                </div>
                <div className="space-y-2 text-gray-600">
                  <p className="font-medium">Address</p>
                  <p className="text-sm sm:text-base">
                    {edetails?.event_location}
                  </p>
                  <p className="mt-2 text-sm sm:text-base">
                    <span className="font-medium">Zipcode:</span>{" "}
                    {edetails?.zipcode}
                  </p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="p-4 bg-white border border-gray-200 shadow-sm rounded-xl sm:p-6">
                <div className="flex items-center mb-4">
                  <HiOutlinePhone className="flex-shrink-0 w-6 h-6 mr-3 text-blue-500" />
                  <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
                    Contact Information
                  </h2>
                </div>
                <div className="text-gray-600">
                  <p className="font-medium">Event Contact</p>
                  <p className="text-sm sm:text-base">
                    {edetails?.event_contact_number}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailView;
